import { action, observable } from 'mobx';
import * as api from '../services/api';

const KUALA_LUMPUR_TIME_ZONE = 'Asia/Kuala_Lumpur';

function getKualaLumpurDateParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: KUALA_LUMPUR_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date());

    return parts.reduce((result, part) => {
        if (part.type !== 'literal') {
            result[part.type] = Number(part.value);
        }
        return result;
    }, {});
}

function getCurrentSchedulingContext() {
    const parts = getKualaLumpurDateParts();
    const today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    const dayOfWeek = (today.getUTCDay() + 6) % 7;

    return {
        dayOfWeek,
        minutes: (parts.hour * 60) + parts.minute,
    };
}

function isCurrentTimeInInterval(interval, context, excludeBreaks) {
    if (!interval) {
        return false;
    }

    const dayOfWeek = Number(interval.day_of_week);
    const startMinutes = Number(interval.start_minutes);
    const endMinutes = Number(interval.end_minutes);
    if (!Number.isInteger(dayOfWeek) || !Number.isInteger(startMinutes) || !Number.isInteger(endMinutes)) {
        return false;
    }

    const normalizedCurrentMinutes = context.dayOfWeek === dayOfWeek ? context.minutes : context.minutes + (24 * 60);
    const isInShift = endMinutes <= 24 * 60 ? (context.dayOfWeek === dayOfWeek && context.minutes >= startMinutes && context.minutes < endMinutes) : 
        (context.dayOfWeek === dayOfWeek && context.minutes >= startMinutes) || 
        (context.dayOfWeek === ((dayOfWeek + 1) % 7) && normalizedCurrentMinutes < endMinutes);

    if (!isInShift) {
        return false;
    }

    if (!excludeBreaks) {
        return true;
    }

    return !(interval.breaks || []).some((breakInterval) => {
        const breakStartMinutes = Number(breakInterval.start_minutes);
        const breakEndMinutes = Number(breakInterval.end_minutes);
        return Number.isInteger(breakStartMinutes) &&
            Number.isInteger(breakEndMinutes) &&
            normalizedCurrentMinutes >= breakStartMinutes &&
            normalizedCurrentMinutes < breakEndMinutes;
    });
}

function getScheduledStaffByHubCodeForCurrentTime(scheduleByHubCode) {
    const context = getCurrentSchedulingContext();
    return Object.entries(scheduleByHubCode || {}).reduce((result, [hubCode, intervals]) => {
        result[hubCode] = {
            excludingBreaks: (intervals || []).filter((interval) => isCurrentTimeInInterval(interval, context, true)).length,
            includingBreaks: (intervals || []).filter((interval) => isCurrentTimeInInterval(interval, context, false)).length,
        };
        return result;
    }, {});
}

class OperationDashboardStore {
    @observable closedPopmealsStores = {};
    @observable closedVirtualBrands = {};
    @observable areas = [];
    @observable businessManagers = [];
    @observable outlets = [];
    @observable territoryManagers = [];
    @observable areaOutletMap = {};
    @observable businessManagerOutletMap = {};
    @observable territoryManagerOutletMap = {};
    @observable noOutletManagerClockedIn = [];
    @observable allIpadsOffline = [];
    @observable outletWithLessMenuItem = [];
    @observable outletWithTopFiveOOS = [];
    @observable receivingNotDone = [];
    @observable cardTerminalNotConnected = [];
    @observable noDitaSalesInTwoHours = [];
    @observable noDeliverySalesInTwoHours = [];
    @observable noMealScanInTwoHours = [];
    @observable noImageInTwoHours = [];
    @observable criticalComponentsOutOfStock = {};
    @observable loading = true;
    @observable dashboardInfoLoading = true;
    @observable underStaffedOutletsWithMinAndClockedInStaff = [];
    @observable slowServiceTimeOutletWithSlowOrderCount = {};

    @action handleGetAreaByOutletData = () => {
        return api.getAreaByOutletData()
            .then((res) => {
                if (res.areas) {
                    this.areas = [
                        { value: "All", label: "All" },
                        ...res.areas.map((area) => ({
                            value: area,
                            label: area,
                        })),
                    ];
                }
                if (res.business_managers) {
                    this.businessManagers = [
                        { value: "All", label: "All" },
                        ...res.business_managers.map((businessManager) => ({
                            value: businessManager,
                            label: businessManager,
                        })),
                    ];
                }
                if (res.outlets) {
                    this.outlets = [
                        { value: "All", label: "All" },
                        ...res.outlets.map((outlet) => ({
                            value: outlet,
                            label: outlet,
                        })),
                    ];
                }
                if (res.territory_managers) {
                    this.territoryManagers = [
                        { value: "All", label: "All" },
                        ...res.territory_managers.map((territoryManager) => ({
                            value: territoryManager,
                            label: territoryManager,
                        })),
                    ];
                }
                this.areaOutletMap = res.area_outlet_map;
                this.businessManagerOutletMap = res.business_manager_outlet_map;
                this.territoryManagerOutletMap = res.territory_manager_outlet_map;
            })
            .catch((error) => {
                console.error("Error fetching area by outlet data:", error);
            }).finally(() => this.loading = false)

    };
    

    @action handleGetOperationDashboardInfo = (filteredOutlets) => {
        this.dashboardInfoLoading = true;
        if (!filteredOutlets || filteredOutlets.length == 0) {
            this.underStaffedOutletsWithMinAndClockedInStaff = [];
            this.dashboardInfoLoading = false;
            return;
        }
        Promise.all([
            api.getOperationDashboardInfo(filteredOutlets),
            api.getOperationDashboardMinStaff()
                .catch((error) => {
                    console.error("Error fetching operation dashboard min staff:", error);
                    return {};
                }),
        ])
            .then(([res, minimumStaffResponse]) => {
                const scheduledStaffByHubCode = getScheduledStaffByHubCodeForCurrentTime(
                    minimumStaffResponse.minimum_staff_schedule_by_hub_code
                );
                this.closedPopmealsStores = res.closed_popmeals_stores;
                this.closedVirtualBrands = res.closed_virtual_brands;
                this.noOutletManagerClockedIn = res.no_outlet_manager_clocked_in;
                this.outletWithLessMenuItem = res.outlet_with_less_menu_item;
                this.outletWithTopFiveOOS = res.outlet_with_top_five_oos;
                this.allIpadsOffline = res.all_ipads_offline;
                this.receivingNotDone = res.receiving_not_done;
                this.cardTerminalNotConnected = res.card_terminal_not_connected;
                this.noDitaSalesInTwoHours = res.no_dita_sales_in_two_hours;
                this.noDeliverySalesInTwoHours = res.no_delivery_sales_in_two_hours;
                this.noMealScanInTwoHours = res.no_meal_scan_in_two_hours;
                this.noImageInTwoHours = res.no_image_in_two_hours;
                this.criticalComponentsOutOfStock = res.critical_components_out_of_stock;
                this.underStaffedOutletsWithMinAndClockedInStaff = (res.under_staffed_outlets_with_min_and_clocked_in_staff || [])
                    .map((outlet) => {
                        const scheduledStaff = scheduledStaffByHubCode[outlet.hub_code];
                        const scheduledExcludingBreaks = scheduledStaff ? scheduledStaff.excludingBreaks : 2;
                        const scheduledIncludingBreaks = scheduledStaff ? scheduledStaff.includingBreaks : scheduledExcludingBreaks;
                        return {
                            ...outlet,
                            scheduled_excluding_breaks: scheduledExcludingBreaks,
                            scheduled_including_breaks: scheduledIncludingBreaks,
                        };
                    })
                    .filter((outlet) => outlet.clocked_in_staff < outlet.scheduled_excluding_breaks);
                this.slowServiceTimeOutletWithSlowOrderCount = res.slow_service_time_outlet_with_slow_order_count;
            })
            .catch((error) => {
                console.error("Error fetching operation dashboard info:", error);
            }).finally(() => this.dashboardInfoLoading = false)
        }
}

const store = new OperationDashboardStore();
export default store;
